import React from 'react';
import './About.css';
import { useLang } from './LanguageContext';

const content = {
    en: {
        title: 'About',
        intro1: 'I am a data professional driven by the challenge of turning raw information into actionable intelligence. With a background spanning public transport and education, I specialize in the intersection of Data Engineering, Machine Learning, and Cloud Infrastructure.',
        intro2: 'Currently, I focus on building robust, end-to-end data ecosystems. My day-to-day involves:',
        items: [
            <>Managing high-performance data warehousing environments in <b>BigQuery</b>.</>,
            <>Orchestrating scalable data pipelines within the <b>Google Cloud Platform (GCP)</b>.</>,
            <>Architecting modular, version-controlled data models using <b>dbt</b>.</>,
            <>Developing interactive, data-driven applications and visualizations in <b>Streamlit</b>.</>,
        ],
        passion: "I am passionate about bridging the gap between backend engineering and frontend visualization—optimizing computational workflows to create solutions that don't just store data, but empower decision-making.",
        skills: 'Strong familiarity with the following:',
        skillList: [
            <><b>Scientific computing</b>: Theory, algorithms and tools for optimization and machine learning</>,
            <><b>Machine Learning</b>: Spark ML, BigQuery ML, sklearn, PyTorch</>,
            <><b>Data analysis</b>: Statistics, pandas, numpy, SQL</>,
            <><b>Data Visualization &amp; BI</b>: Tableau, PowerBI, plotly, matplotlib</>,
            <><b>Data engineering</b>: Airflow, PySpark, Kafka, Dataproc, Dataflow, BigQuery</>,
            <><b>Cloud infrastructure</b>: AWS and GCP</>,
            <><b>Web development</b>: Javascript, ReactJS, Python, Flask</>,
        ],
        interests: 'Interests',
        interestList: [
            'Large Language Models (LLM) for automation',
            'Optimization under Uncertainty',
            'Deep Reinforcement Learning',
            'Sequential Decision Making',
        ],
        publications: 'Publications',
    },
    es: {
        title: 'Acerca de',
        intro1: 'Soy un profesional de datos motivado por el desafío de transformar información en bruto en inteligencia accionable. Con experiencia en transporte público y educación, me especializo en la intersección de Ingeniería de Datos, Machine Learning e Infraestructura en la Nube.',
        intro2: 'Actualmente me enfoco en construir ecosistemas de datos robustos y de punta a punta. Mi trabajo diario involucra:',
        items: [
            <>Gestión de entornos de alta performance de almacenamiento de datos en <b>BigQuery</b>.</>,
            <>Orquestación de pipelines de datos escalables en la <b>Google Cloud Platform (GCP)</b>.</>,
            <>Arquitectura de modelos de datos modulares y versionados usando <b>dbt</b>.</>,
            <>Desarrollo de aplicaciones e visualizaciones interactivas basadas en datos en <b>Streamlit</b>.</>,
        ],
        passion: 'Me apasiona conectar la ingeniería de backend con la visualización frontend, optimizando flujos de trabajo computacionales para crear soluciones que no solo almacenan datos, sino que potencian la toma de decisiones.',
        skills: 'Familiaridad con los siguientes temas:',
        skillList: [
            <><b>Computación científica</b>: Teoría, algoritmos y herramientas para optimización y machine learning</>,
            <><b>Machine Learning</b>: Spark ML, BigQuery ML, sklearn, PyTorch</>,
            <><b>Análisis de datos</b>: Estadística, pandas, numpy, SQL</>,
            <><b>Visualización de datos e BI</b>: Tableau, PowerBI, plotly, matplotlib</>,
            <><b>Ingeniería de datos</b>: Airflow, PySpark, Kafka, Dataproc, Dataflow, BigQuery</>,
            <><b>Infraestructura en la nube</b>: AWS y GCP</>,
            <><b>Desarrollo web</b>: Javascript, ReactJS, Python, Flask</>,
        ],
        interests: 'Intereses',
        interestList: [
            'Modelos de Lenguaje de Gran Escala (LLM) para automatización',
            'Optimización bajo Incertidumbre',
            'Aprendizaje por Refuerzo Profundo',
            'Toma de Decisiones Secuencial',
        ],
        publications: 'Publicaciones',
    },
};

const About = () => {
    const { lang } = useLang();
    const t = content[lang];

    return (
        <div>
            <h1 className="ui header"><span className="hand-drawn-highlight">{t.title}</span></h1>
            <div className="ui stackable grid">
                <div className="twelve wide column">
                    <div className="row">
                        <p>{t.intro1}</p>
                        <p>{t.intro2}</p>
                        <div className="ui bulleted list">
                            {t.items.map((item, i) => (
                                <div className="item" key={i}>{item}</div>
                            ))}
                        </div>
                        <p>{t.passion}</p>
                        <p>{t.skills}</p>
                        <ul>
                            {t.skillList.map((skill, i) => (
                                <li key={i}>{skill}</li>
                            ))}
                        </ul>
                    </div>
                    <a href="https://drive.google.com/file/d/15goz3vioA8yI99H2X5jzXVj9b6wnODcr/view?usp=sharing" target="_blank" rel="noopener noreferrer">
                        <div className='ui button cv-button'>
                            <i className='address card icon'></i> CV
                        </div>
                    </a>
                    <div className="row">
                        <div className="ui divider"></div>
                    </div>
                    <div className="row">
                        <div className="row">
                            <h3 className="ui header"><span className="hand-drawn-highlight">{t.interests}</span></h3>
                        </div>
                        <div className="ui hidden divider"></div>
                        <div className="row">
                            <div className="ui bulleted list">
                                {t.interestList.map((item, i) => (
                                    <div className="item" key={i}>{item}</div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="ui divider"></div>
                    </div>
                    <div className="row">
                        <h3 className="ui header"><span className="hand-drawn-highlight">{t.publications}</span></h3>
                        <div className="ui hidden divider"></div>
                        <div className="row">
                            <div className="ui bulleted list">
                                <div className="item">
                                    <div className="header">
                                        Inventory Control Under Unobserved Losses with Latent State Learning
                                    </div>
                                    Jose Manuel Vera
                                    <div className="description">
                                        <em>7th International Conference on Computer and Communications (ICCC)</em>, 2021. [<a href="https://ieeexplore.ieee.org/abstract/document/9674290">link</a>]
                                    </div>
                                </div>
                                <div className="item">
                                    <div className="header">
                                        Application of the Cross-Entropy Method to the Dynamic Assortment Optimization Problem
                                    </div>
                                    Jose Manuel Vera
                                    <div className="description">
                                        <em>Matemática</em>, vol. 18, no. 1, 2020. [<a href="http://www.revistas.espol.edu.ec/index.php/matematica/article/view/696">link</a>]
                                    </div>
                                </div>
                                <div className="item">
                                    <div className="header">
                                        Deep Reinforcement Learning for Routing a Heterogeneous Fleet of Vehicles
                                    </div>
                                    Jose Manuel Vera, Andres G. Abad
                                    <div className="description">
                                        <em>IEEE Latin American Conference on Computational Intelligence (LA-CCI)</em>, 2019. [<a href="https://ieeexplore.ieee.org/abstract/document/9037042">link</a>]
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="four wide column middle aligned">
                    <img alt="railway" className="ui image about-image" src={require("../images/photo_2.jpg")} />
                </div>
            </div>
        </div>
    );
};

export default About;